import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Select,
  Row,
  Col,
  Radio,
  Space,
} from 'antd';
import { useTranslation } from 'react-i18next';

import CustomModal from '../common/modals/CustomModal';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import { sortByObjProperty } from '../common/helpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

import { filterSelectDropdown, getIdMap } from '../helpers/helpers';

import colors from '../constants/Colors';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import OnTraccrRadioGroup from '../common/inputs/OnTraccrRadioGroup';

export default function EquipmentCheckModal({
  id,
  name,
  currentProjectId,
  currentLocationText,
  onCloseClicked,
  onCheckOut,
  onCheckIn,
  visible,
}) {
  const { t } = useTranslation();
  const equipmentProjects = useSelector((state) => state.equipment.equipmentProjects);
  const equipment = useSelector((state) => state.equipment.equipment);
  const projects = useSelector((state) => state.projects.projects);
  const { settings = {} } = useSelector((state) => state.settings.company) || {};

  const [projectId, setProjectId] = useState(currentProjectId);
  const [location, setLocation] = useState(currentLocationText);
  const [isCustom, setIsCustom] = useState(!!currentLocationText);
  const [showAllProjects, setShowAllProjects] = useState(1);

  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  const ourEquipment = useMemo(() => equipmentIdMap[id], [id, equipmentIdMap]);

  const divisionSet = useMemo(() => new Set(ourEquipment?.divisionIds ?? []), [ourEquipment]);

  const ourProjects = useMemo(() => {
    const { [id]: ourProjectIds = [] } = equipmentProjects;
    return ourProjectIds.map((projId) => {
      const project = projectIdMap[projId];
      if (!project) return null;
      return {
        value: projId,
        label: formatProjectLabelFromCompanySettings({
          name: project.name,
          number: project.number,
          settings,
        }),
        name: project.name,
        number: project.number,
      };
    }).filter(Boolean)
      .sort(sortByObjProperty('label'));
  }, [id, equipmentProjects, projectIdMap, settings]);

  const relevantProjects = useMemo(() => {
    const divProjects = projects
      .filter(({ divisionId, active }) => divisionSet.has(divisionId) && active)
      .map(({ id: projId, name: projName, number }) => ({
        value: projId,
        label: formatProjectLabelFromCompanySettings({ name: projName, number, settings }),
        name: projName,
        number,
      }))
      .sort(sortByObjProperty('label'));

    return divProjects;
  }, [projects, divisionSet, settings]);

  const onSave = useCallback(() => {
    const payload = {};
    if (isCustom) {
      payload.location = location;
    } else {
      payload.projectId = projectId;
    }
    onCheckIn({ id, payload });
  }, [id, isCustom, projectId, location]);
  const onCheckoutClicked = useCallback(() => {
    const payload = {};
    if (isCustom) {
      payload.location = location;
    } else {
      payload.projectId = projectId;
    }
    onCheckOut({ id, payload });
  }, [id, isCustom, projectId, location]);
  const onTextChanged = useCallback((e) => {
    const {
      target: { value } = {},
    } = e;
    setLocation(value);
  }, []);

  useEffect(() => {
    setProjectId(currentProjectId);
  }, [currentProjectId]);

  useEffect(() => {
    setLocation(currentLocationText);
    setIsCustom(!!currentLocationText);
  }, [currentLocationText]);

  const isActive = currentProjectId || currentLocationText;

  const onClose = () => {
    onCloseClicked();
    setShowAllProjects(1);
  };

  return (
    <CustomModal
      title={name}
      onCloseClicked={onClose}
      visible={visible}
      maskColor={colors.MODAL_MASK_DARK}
      width={currentProjectId ? 500 : 400}
    >
      <div id="equipment-check-modal-flavour-text">
        {currentProjectId ? 'Switch' : 'Check In'} equipment
      </div>
      <div style={{ marginBottom: 10 }}>
        <OnTraccrSwitch
          value={isCustom}
          onChange={setIsCustom}
          title={isCustom ? 'Location' : t('Project')}
          containerStyle={{
            display: 'inline-block',
          }}
        />
      </div>
      {
        isCustom
          ? (
            <OnTraccrTextInput
              className="equipment-check-modal-input"
              value={location}
              onChange={onTextChanged}
            />
          )
          : (
            <Col flex="100%">
              <Row style={{ width: '100%', padding: '0px 5px 15px 10px' }}>
                <OnTraccrRadioGroup
                  value={showAllProjects}
                  onChange={setShowAllProjects}
                >
                  <Space direction="vertical" align="start" size="small">
                    <Radio value={0} style={{ textAlign: 'left' }}> Show Equipment Projects </Radio>
                    <Radio value={1} style={{ textAlign: 'left' }}> Show All Projects </Radio>
                  </Space>

                </OnTraccrRadioGroup>
              </Row>

              <Row style={{ flexGrow: 1 }}>
                <Select
                  className="equipment-check-modal-input"
                  placeholder={`Select a ${t('Project').toLowerCase()}`}
                  onSelect={setProjectId}
                  value={projectId}
                  options={showAllProjects ? relevantProjects : ourProjects}
                  showSearch
                  filterOption={filterSelectDropdown}
                />
              </Row>

            </Col>
          )
      }

      <Row
        className="equipment-check-modal-button-container "
        justify={currentProjectId ? 'space-around' : 'center'}
        gutter={12}
      >
        <Col>
          <OnTraccrButton
            type="cancel"
            title="Cancel"
            roundStyle
            onClick={onClose}
          />
        </Col>
        {isActive
          && (
          <Col>
            <OnTraccrButton
              type="back"
              title="Check Out"
              roundStyle
              onClick={onCheckoutClicked}
            />
          </Col>
          )}
        <Col>
          <OnTraccrButton
            title={isActive ? 'Switch' : 'Check In'}
            roundStyle
            disabled={!projectId && !location}
            onClick={onSave}
          />
        </Col>
      </Row>
    </CustomModal>
  );
}
